import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SalesOrderService from '../../services/sales-order.service';
import { addLogoToPDF } from '../../utils/pdfLogoHelper';

const SalesOrderView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [salesOrder, setSalesOrder] = useState<any>(null);
  const [lineItems, setLineItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchSalesOrder();
      fetchLineItems();
    }
  }, [id]);

  const fetchSalesOrder = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await SalesOrderService.getOne(Number(id));
      setSalesOrder(response.data?.data);
    } catch (error: any) {
      console.error('Error fetching sales order:', error);
      setError(error.response?.data?.error?.message || 'Failed to load sales order');
    } finally {
      setLoading(false);
    }
  };

  const fetchLineItems = async () => {
    try {
      const response = await SalesOrderService.getItems(Number(id));
      setLineItems(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching line items:', error);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this sales order?')) {
      try {
        await SalesOrderService.delete(Number(id));
        navigate('/sales-orders');
      } catch (error) {
        console.error('Error deleting sales order:', error);
        alert('Failed to delete sales order');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'success';
      case 'shipped':
        return 'info';
      case 'confirmed':
        return 'primary';
      case 'processing':
        return 'secondary';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return 'OMR 0.00';
    return `OMR ${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!salesOrder) return;

    const doc = new jsPDF();
    const data = salesOrder.attributes;
    const customer = data.customer?.data?.attributes;

    // Load and add logo
    await addLogoToPDF(doc);

    // Add company name
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('DARB NATIONAL SERVICE LLC', 20, 38);

    // Add company header
    doc.setFontSize(20);
    doc.setTextColor(33, 150, 243);
    doc.text('SALES ORDER', 105, 20, { align: 'center' });

    // Add order number and status
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Order #: ${data.orderNumber}`, 20, 35);
    doc.text(`Status: ${(data.status || 'pending').toUpperCase()}`, 20, 42);

    // Add dates
    doc.setFontSize(10);
    doc.text(`Order Date: ${formatDate(data.orderDate)}`, 150, 35);
    doc.text(`Delivery Date: ${formatDate(data.deliveryDate)}`, 150, 42);

    // Add customer information
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Customer Information:', 20, 55);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    let yPos = 62;
    doc.text(customer?.companyName || customer?.contactPerson || '-', 20, yPos);
    if (customer?.email) {
      yPos += 5;
      doc.text(`Email: ${customer.email}`, 20, yPos);
    }
    if (customer?.phone) {
      yPos += 5;
      doc.text(`Phone: ${customer.phone}`, 20, yPos);
    }
    if (customer?.address) {
      yPos += 5;
      doc.text(`Address: ${customer.address}`, 20, yPos);
    }

    // Add line items table
    const tableData = lineItems.map((item) => {
      const attrs = item.attributes;
      const product = attrs?.product?.data?.attributes;
      return [
        product?.name || '-',
        attrs?.quantity || 0,
        `OMR ${(attrs?.unitPrice || 0).toFixed(2)}`,
        `OMR ${(attrs?.discount || 0).toFixed(2)}`,
        `OMR ${(attrs?.tax || 0).toFixed(2)}`,
        `OMR ${(attrs?.total || 0).toFixed(2)}`,
      ];
    });

    autoTable(doc, {
      startY: yPos + 10,
      head: [['Product', 'Qty', 'Unit Price', 'Discount', 'Tax', 'Total']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [33, 150, 243],
        textColor: 255,
        fontStyle: 'bold',
      },
      styles: {
        fontSize: 9,
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { halign: 'right', cellWidth: 20 },
        2: { halign: 'right', cellWidth: 25 },
        3: { halign: 'right', cellWidth: 25 },
        4: { halign: 'right', cellWidth: 25 },
        5: { halign: 'right', cellWidth: 25 },
      },
    });

    // Add totals
    const finalY = (doc as any).lastAutoTable.finalY || yPos + 10;
    doc.setFontSize(10);
    
    doc.text('Subtotal:', 130, finalY + 10);
    doc.text(`OMR ${(data.subtotal || 0).toFixed(2)}`, 180, finalY + 10, { align: 'right' });
    
    doc.text('Discount:', 130, finalY + 17);
    doc.text(`- OMR ${(data.discount || 0).toFixed(2)}`, 180, finalY + 17, { align: 'right' });
    
    doc.text('Tax:', 130, finalY + 24);
    doc.text(`OMR ${(data.tax || 0).toFixed(2)}`, 180, finalY + 24, { align: 'right' });
    
    // Total line
    doc.setDrawColor(33, 150, 243);
    doc.setLineWidth(0.5);
    doc.line(130, finalY + 27, 180, finalY + 27);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Total:', 130, finalY + 34);
    doc.text(`OMR ${(data.total || 0).toFixed(2)}`, 180, finalY + 34, { align: 'right' });

    // Add notes if any
    if (data.notes) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Notes:', 20, finalY + 50);
      doc.setFont('helvetica', 'normal');
      const splitNotes = doc.splitTextToSize(data.notes, 170);
      doc.text(splitNotes, 20, finalY + 57);
    }

    // Save the PDF
    doc.save(`SalesOrder-${data.orderNumber}.pdf`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !salesOrder) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/sales-orders')}
          sx={{ mb: 2 }}
        >
          Back to Sales Orders
        </Button>
        <Alert severity="error">{error || 'Sales order not found'}</Alert>
      </Box>
    );
  }

  const data = salesOrder.attributes;
  const customer = data.customer?.data?.attributes;

  return (
    <Box>
      {/* Print-specific styles */}
      <style>
        {`
          .print-only {
            display: none;
          }
          
          @media print {
            body * {
              visibility: hidden;
            }
            .print-area, .print-area * {
              visibility: visible;
            }
            .print-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            .no-print {
              display: none !important;
            }
            .print-only {
              display: block !important;
            }
            @page {
              margin: 1cm;
            }
          }
        `}
      </style>

      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} className="no-print">
        <Stack direction="row" spacing={2} alignItems="center">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/sales-orders')}>
            Back
          </Button>
          <Typography variant="h4">{data.orderNumber}</Typography>
          <Chip
            label={data.status || 'pending'}
            color={getStatusColor(data.status)}
            size="medium"
          />
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<PdfIcon />} onClick={handleDownloadPDF}>
            Download PDF
          </Button>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}>
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/sales-orders/${id}/edit`)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Stack>
      </Stack>

      {/* Sales Order Details */}
      <Stack spacing={3} className="print-area">
        {/* Print Header - visible only when printing */}
        <Box className="print-only" sx={{ mb: 2 }}>
          <Typography variant="h3" align="center" gutterBottom>
            SALES ORDER
          </Typography>
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Typography variant="h6">{data.orderNumber}</Typography>
            <Chip
              label={data.status || 'pending'}
              color={getStatusColor(data.status)}
              size="small"
            />
          </Stack>
        </Box>

        {/* Header Info */}
        <Paper sx={{ p: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
            <Box flex={1}>
              <Typography variant="h6" gutterBottom>
                Customer Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1}>
                <Typography variant="body1" fontWeight="bold">
                  {customer?.companyName || customer?.contactPerson || '-'}
                </Typography>
                {customer?.email && (
                  <Typography variant="body2" color="text.secondary">
                    {customer.email}
                  </Typography>
                )}
                {customer?.phone && (
                  <Typography variant="body2" color="text.secondary">
                    {customer.phone}
                  </Typography>
                )}
                {customer?.address && (
                  <Typography variant="body2" color="text.secondary">
                    {customer.address}
                  </Typography>
                )}
              </Stack>
            </Box>

            <Box flex={1}>
              <Typography variant="h6" gutterBottom>
                Order Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Order Date:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatDate(data.orderDate)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Delivery Date:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatDate(data.deliveryDate)}
                  </Typography>
                </Box>
                {data.quotation?.data && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      From Quotation:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {data.quotation.data.attributes?.quotationNumber}
                    </Typography>
                  </Box>
                )}
                {data.createdBy?.data && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Created By:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {data.createdBy.data.attributes?.firstName}{' '}
                      {data.createdBy.data.attributes?.lastName}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Stack>
        </Paper>

        {/* Line Items */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Items
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Discount</TableCell>
                  <TableCell align="right">Tax</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No items
                    </TableCell>
                  </TableRow>
                ) : (
                  lineItems.map((item) => {
                    const attrs = item.attributes;
                    const product = attrs?.product?.data?.attributes;
                    
                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {product?.name || '-'}
                          </Typography>
                          {attrs?.description && (
                            <Typography variant="caption" color="text.secondary">
                              {attrs.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{attrs?.quantity}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(attrs?.unitPrice)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(attrs?.discount)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(attrs?.tax)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(attrs?.total)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Totals */}
        <Card variant="outlined" sx={{ bgcolor: 'primary.light' }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1" color="primary.contrastText">
                  Subtotal:
                </Typography>
                <Typography variant="h6" color="primary.contrastText">
                  {formatCurrency(data.subtotal)}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1" color="primary.contrastText">
                  Discount:
                </Typography>
                <Typography variant="h6" color="primary.contrastText">
                  - {formatCurrency(data.discount)}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1" color="primary.contrastText">
                  Tax:
                </Typography>
                <Typography variant="h6" color="primary.contrastText">
                  {formatCurrency(data.tax)}
                </Typography>
              </Stack>
              <Divider sx={{ borderColor: 'primary.contrastText', opacity: 0.3 }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h5" color="primary.contrastText" fontWeight="bold">
                  Total:
                </Typography>
                <Typography variant="h3" color="primary.contrastText" fontWeight="bold">
                  {formatCurrency(data.total)}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Notes */}
        {data.notes && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Notes
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {data.notes}
            </Typography>
          </Paper>
        )}
      </Stack>
    </Box>
  );
};

export default SalesOrderView;

